import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import uuid from 'uuid/v1';
import { isOnline } from 'services/callAjax/isOnline';
import { tabId } from '../../app-events-aggregator/tabId';
import { authentificationSelector, authTokenManager } from '../../authentication';
import { throwCriticalError } from '../../formData/utils/throwCriticalError';

const WEB_SOCKET_URL = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/api-ws/`;
const RECONNECT_ATTEMPTS = 5;
const RECONNECT_INTERVAL = 3000;
const PING = 'ping';
const PONG = 'pong';
const PING_PONG_INTERVAL = 60000;

export const useWebSocketConnection = () => {
    const isAuthenticated = useSelector(authentificationSelector);
    const [online, setOnline] = useState(isOnline());
    const shouldConnect = isAuthenticated && online;
    const listenersRef = useRef({});
    const onMessage = useCallback(message => {
        if (message?.data === PONG) {
            return;
        }
        Object.values(listenersRef.current).forEach(listener => listener(message));
    }, []);

    const { sendJsonMessage, sendMessage, readyState } = useWebSocket(
        WEB_SOCKET_URL,
        {
            protocols: [encodeURIComponent(authTokenManager.getToken())],
            queryParams: {
                contextId: tabId,
            },
            onMessage,
            onError: error => console.error('Web socket connect error', error),
            shouldReconnect: () => true,
            reconnectAttempts: RECONNECT_ATTEMPTS,
            reconnectInterval: RECONNECT_INTERVAL,
            share: true,
        },
        shouldConnect
    );
    const connected = readyState === ReadyState.OPEN;

    const registerListener = useCallback((newListener, id = uuid()) => {
        if (listenersRef.current[id]) {
            throwCriticalError(new Error(`WebSocket listener with ID ${id} already exists.`));
            return;
        }

        listenersRef.current[id] = newListener;

        return (providedId = id) => {
            if (!providedId || !listenersRef.current[providedId]) {
                return;
            }

            const { [providedId]: _, ...rest } = listenersRef.current;
            listenersRef.current = rest ? rest : {};
        };
    }, []);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setOnline(isOnline());
        }, RECONNECT_INTERVAL);
        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        if (!connected) {
            return;
        }

        const pingIntervalId = setInterval(() => {
            sendMessage(PING);
        }, PING_PONG_INTERVAL);
        return () => clearInterval(pingIntervalId);
    }, [connected, sendMessage]);

    return {
        onMessage: registerListener,
        sendMessage: sendJsonMessage,
        connected,
    };
};
